
import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Save, ZoomIn, ZoomOut, Move, PenTool, RotateCcw, Square, Circle, Disc, Crop } from 'lucide-react';
import { Button } from './Button';
import { ImageCropper } from './ImageCropper';

interface Props {
  initialImage: string;
  onSave: (newImage: string) => void;
  onClose: () => void;
}

type Mode = 'draw' | 'pan' | 'rect' | 'circle' | 'dot-red' | 'dot-green';

export const ImageEditor: React.FC<Props> = ({ initialImage, onSave, onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [mode, setMode] = useState<Mode>('draw');
  const [color, setColor] = useState('#ef4444');
  const drawingRef = useRef(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [snapshot, setSnapshot] = useState<ImageData | null>(null);
  const [currentImage, setCurrentImage] = useState(initialImage);
  const [showCropper, setShowCropper] = useState(false);
  
  // Load image onto canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = currentImage;
    img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        // Default settings
        ctx.strokeStyle = color; // Dynamic
        ctx.lineWidth = Math.max(5, img.width / 200); 
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
    };
  }, [currentImage]);

  // Pointer-Events statt Touch/Maus-Paar. React haengt touchmove PASSIV an,
  // ein preventDefault dort wirkt nicht — auf dem Handy scrollte der
  // overflow-auto-Container beim Zeichnen mit und der Strich lief dem Finger
  // davon. touch-action:none am Canvas (unten) nimmt dem Browser das Scrollen
  // im Zeichenmodus ab; setPointerCapture haelt den Strich auch, wenn der
  // Finger kurz ueber den Rand rutscht.
  const getCoords = (e: React.PointerEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    return {
        x: (e.clientX - rect.left) * (canvas.width / rect.width),
        y: (e.clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDrawing = (e: React.PointerEvent) => {
    if (mode === 'pan') return;
    // Nur der erste Finger zeichnet; ein zweiter (Zoom-Geste) startet keinen Strich.
    if (!e.isPrimary) return;
    e.preventDefault();
    
    const { x, y } = getCoords(e);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if(!ctx || !canvas) return;

    try { canvas.setPointerCapture(e.pointerId); } catch { /* aeltere Browser */ }
    drawingRef.current = true;
    setStartPos({ x, y });

    // For shapes, save current state to restore on drag (preview)
    if (mode === 'rect' || mode === 'circle') {
        setSnapshot(ctx.getImageData(0, 0, canvas.width, canvas.height));
    }

    // For dots, draw immediately
    if (mode === 'dot-red' || mode === 'dot-green') {
        const radius = Math.max(10, canvas.width / 100);
        ctx.fillStyle = mode === 'dot-red' ? '#ef4444' : '#22c55e';
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 2;
        ctx.stroke();
        drawingRef.current = false;
        // Immediate action
    }

    if (mode === 'draw') {
        ctx.strokeStyle = color; 
        ctx.lineWidth = Math.max(5, canvas.width / 200);
        ctx.beginPath();
        ctx.moveTo(x, y);
    }
  };

  const draw = (e: React.PointerEvent) => {
    // Ref statt State: das erste pointermove kommt oft noch vor dem Re-Render
    // nach pointerdown — mit dem State fehlte der Anfang jedes Strichs.
    if (!drawingRef.current || mode === 'pan' || !e.isPrimary) return;
    e.preventDefault(); 
    
    const { x, y } = getCoords(e);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if(!ctx || !canvas) return;

    if (mode === 'draw') {
        ctx.lineTo(x, y);
        ctx.stroke();
    } 
    
    else if (mode === 'rect' && snapshot) {
        ctx.putImageData(snapshot, 0, 0); // Clear to start
        ctx.strokeStyle = color;
        ctx.lineWidth = Math.max(5, canvas.width / 200);
        const w = x - startPos.x;
        const h = y - startPos.y;
        ctx.strokeRect(startPos.x, startPos.y, w, h);
    }

    else if (mode === 'circle' && snapshot) {
        ctx.putImageData(snapshot, 0, 0);
        ctx.strokeStyle = color;
        ctx.lineWidth = Math.max(5, canvas.width / 200);
        const radius = Math.sqrt(Math.pow(x - startPos.x, 2) + Math.pow(y - startPos.y, 2));
        ctx.beginPath();
        ctx.arc(startPos.x, startPos.y, radius, 0, 2 * Math.PI);
        ctx.stroke();
    }
  };

  const stopDrawing = (e?: React.PointerEvent) => {
    if (e) { try { canvasRef.current?.releasePointerCapture(e.pointerId); } catch { /* nicht gefangen */ } }
    drawingRef.current = false;
    setSnapshot(null);
    const ctx = canvasRef.current?.getContext('2d');
    if(mode === 'draw') ctx?.closePath();
  };

  const handleSave = () => {
      if (canvasRef.current) {
          const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.8);
          onSave(dataUrl);
      }
  };

  const resetCanvas = () => {
      setCurrentImage(initialImage);
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (canvas && ctx) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = initialImage;
          img.onload = () => {
             ctx.clearRect(0, 0, canvas.width, canvas.height);
             ctx.drawImage(img, 0, 0);
          };
      }
  };

  /*
   * Portal an document.body — wie AdditionalServicesModal. Das Begehungsmodul
   * traegt animate-scale-in mit fill-mode both: das stehende `transform`
   * oeffnet einen eigenen Stacking-Context, in dem z-[70] gegen die Sidebar
   * (Geschwister auf Wurzelebene) wirkungslos ist — die Werkzeugleiste lag am
   * Desktop links unter der Seitenleiste und war nicht erreichbar.
   */
  return createPortal((
    <div className="fixed inset-0 z-[300] bg-slate-900 flex flex-col animate-fade-in">
      {/* Werkzeugleiste: NUR Werkzeuge, darf horizontal scrollen. Speichern und
          Abbrechen stehen unten in einer festen Fussleiste — vorher lagen sie am
          rechten Ende dieser Leiste und waren am Handy erst nach langem Wischen
          erreichbar. */}
      <div className="bg-slate-800 p-3 md:p-4 flex items-center text-white shrink-0 shadow-md z-10 overflow-x-auto">
         <div className="flex items-center gap-3 md:gap-4">
             <div className="flex bg-slate-700 rounded-lg p-1 gap-1">
                 <button 
                    onClick={() => setMode('draw')} 
                    className={`p-2 rounded ${mode === 'draw' ? 'bg-blue-600 text-white shadow' : 'text-slate-300 hover:text-white'}`}
                    title="Freihand"
                 >
                     <PenTool className="w-5 h-5"/>
                 </button>
                 <button 
                    onClick={() => setMode('rect')} 
                    className={`p-2 rounded ${mode === 'rect' ? 'bg-blue-600 text-white shadow' : 'text-slate-300 hover:text-white'}`}
                    title="Rechteck"
                 >
                     <Square className="w-5 h-5"/>
                 </button>
                 <button 
                    onClick={() => setMode('circle')} 
                    className={`p-2 rounded ${mode === 'circle' ? 'bg-blue-600 text-white shadow' : 'text-slate-300 hover:text-white'}`}
                    title="Kreis"
                 >
                     <Circle className="w-5 h-5"/>
                 </button>
                 <div className="w-px bg-slate-600 mx-1"></div>
                 <button 
                    onClick={() => setColor('#ef4444')} 
                    className={`p-2 rounded ${color === '#ef4444' ? 'bg-slate-900 border border-slate-600' : ''}`}
                    title="Rot"
                 >
                     <div className="w-4 h-4 rounded-full bg-red-500"></div>
                 </button>
                 <button 
                    onClick={() => setColor('#22c55e')} 
                    className={`p-2 rounded ${color === '#22c55e' ? 'bg-slate-900 border border-slate-600' : ''}`}
                    title="Grün"
                 >
                     <div className="w-4 h-4 rounded-full bg-green-500"></div>
                 </button>
                 <div className="w-px bg-slate-600 mx-1"></div>
                 <button 
                    onClick={() => setMode('dot-red')} 
                    className={`p-2 rounded ${mode === 'dot-red' ? 'bg-blue-600 text-white shadow' : 'text-slate-300 hover:text-white'}`}
                    title="Roter Punkt (Fehler)"
                 >
                     <Disc className="w-5 h-5 text-red-500"/>
                 </button>
                 <button 
                    onClick={() => setMode('dot-green')} 
                    className={`p-2 rounded ${mode === 'dot-green' ? 'bg-blue-600 text-white shadow' : 'text-slate-300 hover:text-white'}`}
                    title="Grüner Punkt (OK)"
                 >
                     <Disc className="w-5 h-5 text-green-500"/>
                 </button>
                 <div className="w-px bg-slate-600 mx-1"></div>
                 <button 
                    onClick={() => setMode('pan')} 
                    className={`p-2 rounded ${mode === 'pan' ? 'bg-blue-600 text-white shadow' : 'text-slate-300 hover:text-white'}`}
                    title="Verschieben (Scrollen)"
                 >
                     <Move className="w-5 h-5"/>
                 </button>
                 <div className="w-px bg-slate-600 mx-1"></div>
                 <button 
                    onClick={() => setShowCropper(true)} 
                    className="p-2 rounded text-slate-300 hover:text-white"
                    title="Zuschneiden"
                 >
                     <Crop className="w-5 h-5"/>
                 </button>
             </div>
             
             <div className="flex bg-slate-700 rounded-lg p-1">
                 <button onClick={() => setScale(s => Math.max(0.2, s - 0.2))} className="p-2 hover:bg-slate-600 rounded"><ZoomOut className="w-5 h-5"/></button>
                 <span className="p-2 text-xs font-mono w-12 text-center">{Math.round(scale * 100)}%</span>
                 <button onClick={() => setScale(s => Math.min(3, s + 0.2))} className="p-2 hover:bg-slate-600 rounded"><ZoomIn className="w-5 h-5"/></button>
             </div>

             <button onClick={resetCanvas} className="p-2 hover:bg-slate-700 rounded text-slate-300" title="Zurücksetzen">
                 <RotateCcw className="w-5 h-5"/>
             </button>
         </div>
      </div>

      {/* Canvas Area */}
      <div 
        ref={containerRef}
        className={`flex-1 overflow-auto overscroll-contain bg-slate-900 flex items-center justify-center p-8 ${mode === 'pan' ? 'cursor-grab active:cursor-grabbing' : 'cursor-crosshair'}`}
      >
         <canvas 
            ref={canvasRef}
            onPointerDown={startDrawing}
            onPointerMove={draw}
            onPointerUp={stopDrawing}
            onPointerCancel={stopDrawing}
            onPointerLeave={stopDrawing}
            onContextMenu={(e) => e.preventDefault()}
            style={{ 
                // Zeichnen: Browser darf nicht scrollen/zoomen. Verschieben-Modus:
                // Finger scrollt den Container wie gewohnt.
                touchAction: mode === 'pan' ? 'auto' : 'none',
                transform: `scale(${scale})`, 
                transformOrigin: 'center center',
                transition: 'transform 0.1s ease-out',
                boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.5)'
            }}
         />
      </div>

      {/* Fussleiste: immer sichtbar, Daumenweite am Handy; rechtsbuendig am Desktop. */}
      <div className="bg-slate-800 border-t border-slate-700 p-3 md:p-4 shrink-0 grid grid-cols-2 gap-3 sm:flex sm:justify-end pb-safe">
          <Button variant="ghost" onClick={onClose} className="text-white hover:bg-slate-700 w-full sm:w-auto justify-center">Abbrechen</Button>
          <Button variant="primary" onClick={handleSave} icon={<Save className="w-4 h-4"/>} className="w-full sm:w-auto justify-center">Speichern</Button>
      </div>

      {showCropper && (
        <div className="fixed inset-0 z-[310] bg-slate-900 flex flex-col">
            <ImageCropper 
              image={currentImage} 
              onSave={(cropped) => {
                setCurrentImage(cropped);
                setShowCropper(false);
              }} 
              onCancel={() => setShowCropper(false)}
            />
        </div>
      )}
    </div>
  ), document.body);
};
